import React, { useEffect, useMemo, useState } from 'react';
import { Loader, Modal, TextInput, UnstyledButton } from '@mantine/core';
import { AdminPaymentlineView } from '../../../../services/admin-payments/admin-payments.service';
import { resolveTip } from 'shared-models/browser';
import { orderlineDisplayName, OrderlineView } from '../../../../services/orderlines/orderlines.service';
import { OrderView } from '../../../../services/orders/orders.service';
import { ordersService, OrderPrintLogView } from '../../../../services/orders/orders.service';
import { ReportView } from '../../../../services/reports/reports.service';
import { TotalsMode } from '../../shared/types/dashboard-page.types';
import { asCurrency, asHour, computeGlobalDiscountAmount, sum } from '../../shared/utils/dashboard-page.utils';
import classes from './breakdown-row-modal.module.scss';

type SalesOrderRow = {
  orderId: number;
  tenantId: string;
  orderCode: string;
  tableLabel: string;
  waiterName: string;
  amount: number;
  tip: number;
  updatedAt: string;
  dinersCount: number;
  status: 'open' | 'closed';
};

export type SalesSummaryModalContext = {
  selectedCard: { key: TotalsMode; title: string; value: number; count?: number };
  report: ReportView | null;
  orders: OrderView[];
  closedOrderDinersByCode: Record<string, number>;
  payments: AdminPaymentlineView[];
};

export type BreakdownRowModalProps = {
  selectedSummary: SalesSummaryModalContext | null;
  onClose: () => void;
};

const getStatusScopedPayments = (
  cardMode: TotalsMode,
  rows: AdminPaymentlineView[],
): AdminPaymentlineView[] => {
  const doneRows = rows.filter((row) => row.status === 'done');
  if (cardMode === 'openOrders') {
    return doneRows.filter((row) => row.orderStatus === 'open');
  }
  if (cardMode === 'closedOrders' || cardMode === 'closedShift') {
    return doneRows.filter((row) => row.orderStatus === 'closed');
  }
  return doneRows;
};

const toOpenOrderRows = (rows: OrderView[]): SalesOrderRow[] => rows.map((order) => ({
  orderId: order.id,
  tenantId: order.tenantId,
  orderCode: order.orderCode,
  tableLabel: order.tableNumber ? `שולחן ${order.tableNumber}` : (order.orderType === 'take_away' ? 'טייק אווי' : 'ללא שולחן'),
  waiterName: order.guestName?.trim() || '—',
  amount: order.totalAmount ?? 0,
  tip: 0,
  updatedAt: order.lastActivityAt ?? order.updatedAt ?? order.createdAt,
  dinersCount: order.dinersCount ?? 0,
  status: 'open',
}));

const collectedTip = (payment: AdminPaymentlineView): number =>
  (payment.globalTipCollected ?? 0) + resolveTip(payment.tipInputMode ?? null, payment.tipInputValue ?? null, payment.baseAmount);

const toClosedPaymentRows = (rows: AdminPaymentlineView[], dinersByCode: Record<string, number>): SalesOrderRow[] => {
  const grouped = new Map<string, SalesOrderRow>();
  rows.forEach((payment) => {
    const current = grouped.get(payment.orderCode);
    const waiterName = payment.createdByName ?? payment.closedByName ?? payment.changedByName ?? '—';
    if (!current) {
      grouped.set(payment.orderCode, {
        orderId: payment.orderId,
        tenantId: payment.tenantId,
        orderCode: payment.orderCode,
        tableLabel: payment.tableNumber ? `שולחן ${payment.tableNumber}` : 'ללא שולחן',
        waiterName,
        amount: payment.baseAmount,
        tip: collectedTip(payment),
        updatedAt: payment.updatedAt,
        dinersCount: dinersByCode[payment.orderCode] ?? 0,
        status: 'closed',
      });
      return;
    }
    current.amount += payment.baseAmount;
    current.tip += collectedTip(payment);
    if (!current.waiterName || current.waiterName === '—') {
      current.waiterName = waiterName;
    }
    if (new Date(payment.updatedAt).getTime() > new Date(current.updatedAt).getTime()) {
      current.updatedAt = payment.updatedAt;
    }
  });
  return [...grouped.values()];
};

const printKindLabel = (kind: OrderPrintLogView['kind']): string => {
  if (kind === 'reprint') {
    return 'הדפסה חוזרת';
  }
  if (kind === 'receipt_print') {
    return 'הדפסת חשבון';
  }
  if (kind === 'red_fire') {
    return 'RED - דילוג';
  }
  return 'הדפסת מטבח';
};

const paymentMethodLabel = (row: AdminPaymentlineView): string => {
  if (row.method === 'cash') {
    return 'מזומן';
  }

  const sessionNumberMatch = row.sessionName.match(/(?:pos|נייח)\s*\/?\s*(\d+)/i);
  if (sessionNumberMatch?.[1]) {
    return `נייח ${sessionNumberMatch[1]}`;
  }

  return 'אשראי ידני';
};

const displayOrderCode = (orderCode: string): string => orderCode.split('/').slice(-2).join('/');

export const BreakdownRowModal: React.FC<BreakdownRowModalProps> = ({
  selectedSummary,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<SalesOrderRow | null>(null);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState<string | null>(null);
  const [orderView, setOrderView] = useState<OrderView | null>(null);
  const [orderlines, setOrderlines] = useState<OrderlineView[]>([]);
  const [printLogs, setPrintLogs] = useState<OrderPrintLogView[]>([]);
  const [expandedPrintLogIds, setExpandedPrintLogIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    setQuery('');
  }, [selectedSummary?.selectedCard.key]);

  useEffect(() => {
    setSelectedOrder(null);
    setOrderView(null);
    setOrderlines([]);
    setPrintLogs([]);
    setExpandedPrintLogIds(new Set());
    setOrderDetailsError(null);
    setOrderDetailsLoading(false);
  }, [selectedSummary?.selectedCard.key]);

  const togglePrintLogItems = (logId: number) => {
    setExpandedPrintLogIds((previous) => {
      const next = new Set(previous);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!selectedOrder) {
      return;
    }

    let cancelled = false;
    const loadOrderDetails = async () => {
      setOrderDetailsLoading(true);
      setOrderDetailsError(null);
      try {
        const details = await ordersService.getDashboardOrderDetails(selectedOrder.tenantId, selectedOrder.orderId);

        if (cancelled) {
          return;
        }
        setOrderView(details.order);
        setOrderlines(details.orderlines);
        setPrintLogs(details.printLogs);
      } catch {
        if (!cancelled) {
          setOrderDetailsError('טעינת פרטי ההזמנה נכשלה');
          setOrderView(null);
          setOrderlines([]);
          setPrintLogs([]);
        }
      } finally {
        if (!cancelled) {
          setOrderDetailsLoading(false);
        }
      }
    };

    void loadOrderDetails();

    return () => {
      cancelled = true;
    };
  }, [selectedOrder]);

  const scopedDonePayments = useMemo(
    () => (selectedSummary ? getStatusScopedPayments(selectedSummary.selectedCard.key, selectedSummary.payments) : []),
    [selectedSummary],
  );

  const orderRows = useMemo(() => {
    if (!selectedSummary) {
      return [] as SalesOrderRow[];
    }

    const openRows = toOpenOrderRows(selectedSummary.orders);
    const closedRows = toClosedPaymentRows(
      selectedSummary.payments.filter((row) => row.status === 'done' && row.orderStatus === 'closed'),
      selectedSummary.closedOrderDinersByCode,
    );

    const rows = (
      selectedSummary.selectedCard.key === 'openOrders'
        ? openRows
        : selectedSummary.selectedCard.key === 'closedOrders' || selectedSummary.selectedCard.key === 'closedShift'
          ? closedRows
          : [...closedRows, ...openRows]
    ).sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );

    const needle = query.trim().toLowerCase();
    if (!needle) {
      return rows;
    }

    return rows.filter((row) => {
      const haystack = `${row.orderCode} ${row.tableLabel} ${row.waiterName}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [query, selectedSummary]);

  const summary = useMemo(() => {
    if (!selectedSummary) {
      return null;
    }
    const report = selectedSummary.report;
    const isOpenCard = selectedSummary.selectedCard.key === 'openOrders';
    const isAllCard = selectedSummary.selectedCard.key === 'ordersTotal';
    const openDinersCount = sum(selectedSummary.orders.map((order) => order.dinersCount ?? 0));

    let sales: number;
    let tipsTotal: number;
    let ordersCount: number;
    let dinersCount: number;
    let avgPerDiner: number;
    let food: number;
    let beverages: number;

    if (isOpenCard) {
      sales = sum(selectedSummary.orders.map((order) => order.totalAmount ?? 0));
      tipsTotal = 0;
      ordersCount = selectedSummary.orders.length;
      dinersCount = openDinersCount;
      avgPerDiner = dinersCount > 0 ? sales / dinersCount : 0;
      food = report?.openOrdersSalesByReportGroup.food ?? 0;
      beverages = report?.openOrdersSalesByReportGroup.beverages ?? 0;
    } else if (isAllCard) {
      sales = selectedSummary.selectedCard.value;
      tipsTotal = report?.tipsTotal ?? 0;
      ordersCount = selectedSummary.selectedCard.count ?? new Set(scopedDonePayments.map((row) => row.orderCode)).size;
      dinersCount = (report?.dinersCount ?? 0) + openDinersCount;
      avgPerDiner = dinersCount > 0 ? sales / dinersCount : 0;
      food = (report?.salesByReportGroup.food ?? 0) + (report?.openOrdersSalesByReportGroup.food ?? 0);
      beverages = (report?.salesByReportGroup.beverages ?? 0) + (report?.openOrdersSalesByReportGroup.beverages ?? 0);
    } else {
      sales = selectedSummary.selectedCard.value;
      tipsTotal = report?.tipsTotal ?? 0;
      ordersCount = selectedSummary.selectedCard.count ?? new Set(scopedDonePayments.map((row) => row.orderCode)).size;
      dinersCount = report?.dinersCount ?? 0;
      avgPerDiner = report?.avgPerDiner ?? (ordersCount > 0 ? (sales + tipsTotal) / ordersCount : 0);
      food = report?.salesByReportGroup.food ?? 0;
      beverages = report?.salesByReportGroup.beverages ?? 0;
    }

    const totalWithTips = sales + tipsTotal;
    const cash = sum(scopedDonePayments.filter((row) => row.method === 'cash').map((row) => row.baseAmount));
    const credit = sum(scopedDonePayments.filter((row) => row.method === 'credit').map((row) => row.baseAmount));

    return {
      totalWithTips,
      ordersCount,
      avgPerDiner,
      dinersCount,
      sales,
      tips: tipsTotal,
      food,
      beverages,
      cash,
      credit,
    };
  }, [scopedDonePayments, selectedSummary]);

  const selectedOrderPayments = useMemo(() => {
    if (!selectedSummary || !selectedOrder) {
      return [] as AdminPaymentlineView[];
    }
    return selectedSummary.payments.filter((row) => row.orderCode === selectedOrder.orderCode && row.status === 'done');
  }, [selectedOrder, selectedSummary]);

  const globalDiscount = useMemo(() => {
    if (!orderView || orderView.discountMode == null || orderView.discountInput == null) {
      return null;
    }
    const netItemsTotal = sum(orderlines.map((line) => line.unitPrice * line.quantity - (line.discountInput ?? 0)));
    const amount = computeGlobalDiscountAmount(netItemsTotal, orderView.discountMode, orderView.discountInput);
    return { pct: orderView.discountMode === 'percentage' ? orderView.discountInput : null, amount };
  }, [orderView, orderlines]);

  return (
    <Modal
      opened={Boolean(selectedSummary)}
      onClose={onClose}
      title="פירוט מכירות"
      size="58rem"
      classNames={{
        inner: classes.modalInner,
        content: classes.modalContent,
        header: classes.modalHeader,
        title: classes.modalTitle,
        body: classes.modalBody,
        close: classes.modalClose,
      }}
      centered
      closeButtonProps={{ 'data-testid': 'dashboard-breakdown-close' } as Record<string, string>}
    >
      {selectedSummary && summary ? (
        <div className={classes.dialogBody}>
          <div className={classes.searchRow}>
            <TextInput
              classNames={{ root: classes.searchInputRoot, input: classes.searchInput }}
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="הקלד שם שולחן, מלצר או מוצר"
              aria-label="חיפוש הזמנות"
              data-testid="dashboard-breakdown-search"
              size="md"
            />
          </div>

          <h3 className={classes.sectionTitle}>סיכום</h3>
          <div className={classes.summaryColumns}>
            <div className={classes.summaryCell}><span>סה"כ עם טיפ</span><strong>{asCurrency(summary.totalWithTips)}</strong></div>
            <div className={classes.summaryCell}><span>סועדים</span><strong>{summary.dinersCount}</strong></div>
            <div className={classes.summaryCell}><span>ממוצע לסועד</span><strong>{asCurrency(summary.avgPerDiner)}</strong></div>
            <div className={classes.summaryCell}><span>טיפ</span><strong>{asCurrency(summary.tips)}</strong></div>
            <div className={classes.summaryCell}><span>מכירות</span><strong>{asCurrency(summary.sales)}</strong></div>
            <div className={classes.summaryCell}><span>הזמנות</span><strong>{summary.ordersCount}</strong></div>
            <div className={classes.summaryCell}><span>אוכל</span><strong>{asCurrency(summary.food)}</strong></div>
            <div className={classes.summaryCell}><span>משקאות</span><strong>{asCurrency(summary.beverages)}</strong></div>
          </div>

          <h3 className={classes.sectionTitle}>הזמנות</h3>
          <p className={classes.sectionHelp}>לחיצה על כרטיס הזמנה תציג את פרטי ההזמנה</p>

          <div className={classes.ordersList}>
            {orderRows.length === 0 ? (
              <div className={classes.emptyState}>לא נמצאו הזמנות עבור החיפוש.</div>
            ) : (
              orderRows.map((row) => {
                const totalWithTip = row.amount + row.tip;
                const salesOnly = row.amount;
                const avgPerDiner = row.dinersCount > 0 ? salesOnly / row.dinersCount : null;

                return (
                  <UnstyledButton
                    key={row.orderCode}
                    className={classes.orderCardButton}
                    onClick={() => setSelectedOrder(row)}
                    data-testid={`dashboard-breakdown-order-${row.orderCode}`}
                  >
                    <article className={classes.orderCard}>
                      <div className={classes.orderMetric}><span className={classes.label}>שולחן</span><strong className={classes.valueAccent}>{row.tableLabel}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>שעה</span><strong>{asHour(row.updatedAt)}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>מלצר</span><strong>{row.waiterName}</strong></div>

                      <div className={classes.orderMetric}><span className={classes.label}>סה"כ עם טיפ</span><strong>{asCurrency(totalWithTip)}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>סועדים</span><strong>{row.dinersCount > 0 ? row.dinersCount : '—'}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>ממוצע</span><strong>{avgPerDiner === null ? '—' : asCurrency(avgPerDiner)}</strong></div>

                      <div className={classes.orderMetric}><span className={classes.label}>טיפ</span><strong>{asCurrency(row.tip)}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>מכירות</span><strong>{asCurrency(salesOnly)}</strong></div>
                      <div className={classes.orderMetric}><span className={classes.label}>סטטוס</span><strong>{row.status === 'open' ? 'פתוחה' : 'סגורה'}</strong></div>
                    </article>
                  </UnstyledButton>
                );
              })
            )}
          </div>
        </div>
      ) : null}

      <Modal
        opened={Boolean(selectedOrder)}
        onClose={() => setSelectedOrder(null)}
        title={selectedOrder ? `פרטי הזמנה - ${selectedOrder.tableLabel}` : 'פרטי הזמנה'}
        size="54rem"
        classNames={{
          content: classes.detailsModalContent,
          header: classes.detailsModalHeader,
          title: classes.detailsModalTitle,
          body: classes.detailsModalBody,
          close: classes.modalClose,
        }}
        centered
        closeButtonProps={{ 'data-testid': 'dashboard-breakdown-detail-close' } as Record<string, string>}
      >
        {selectedOrder ? (
          <div className={classes.detailsGrid}>
            {orderDetailsLoading ? <div className={classes.detailsLoader}><Loader size="sm" color="gray" /></div> : null}
            {orderDetailsError ? <p className={classes.detailsError}>{orderDetailsError}</p> : null}

            <section className={classes.detailsSummaryCard}>
              <div className={classes.detailsStat}><span>מזהה הזמנה</span><strong>{displayOrderCode(selectedOrder.orderCode)}</strong></div>
              <div className={classes.detailsStat}><span>שולחן</span><strong>{selectedOrder.tableLabel}</strong></div>
              <div className={classes.detailsStat}><span>מלצר</span><strong>{selectedOrder.waiterName}</strong></div>
              <div className={classes.detailsStat}><span>שעה</span><strong>{asHour(selectedOrder.updatedAt)}</strong></div>
              <div className={classes.detailsStat}><span>סועדים</span><strong>{(orderView?.dinersCount ?? selectedOrder.dinersCount) > 0 ? (orderView?.dinersCount ?? selectedOrder.dinersCount) : '—'}</strong></div>
              <div className={classes.detailsStat}><span>סה"כ</span><strong>{asCurrency(selectedOrder.amount)}</strong></div>
              <div className={classes.lastRow}>
              <div className={classes.detailsStat}><span>טיפ</span><strong>{asCurrency(selectedOrder.tip)}</strong></div>
              <div className={classes.detailsStat}><span>הנחה</span><strong>{asCurrency(globalDiscount?.amount ?? 0)}</strong></div>
              </div>
            </section>

            <div className={classes.detailsSectionGroup}>
              <h4 className={classes.detailsSectionTitle}>פריטים</h4>
              <section className={`${classes.detailsSection} ${classes.detailsSectionCardTone}`}>
                {orderlines.length === 0 ? (
                  <p className={classes.detailsEmpty}>אין פריטים להצגה</p>
                ) : (
                  <div className={classes.itemsList}>
                    <div className={classes.itemHeaderRow}>
                      <span className={classes.itemHeaderName}>פריט</span>
                      <span className={classes.itemHeaderValue}>מחיר</span>
                      <span className={classes.itemHeaderValue}>סה"כ</span>
                    </div>
                    {orderlines.map((line) => (
                      <article key={line.id} className={classes.itemRow}>
                        <span className={classes.itemName}>
                          <span className={classes.itemNameLane}>
                            <span className={classes.quantityInline}>{`x ${line.quantity}`}</span>
                            {' '}
                            {orderlineDisplayName(line)}
                          </span>
                        </span>
                        <span className={classes.itemValue}>{asCurrency(line.unitPrice)}</span>
                        <span className={classes.itemValue}>{asCurrency(line.unitPrice * line.quantity - (line.discountInput ?? 0))}</span>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </div>

            <div className={classes.detailsSectionGroup}>
              <h4 className={classes.detailsSectionTitle}>תשלומים</h4>
              <section className={`${classes.detailsSection} ${classes.detailsSectionCardTone}`}>
                {selectedOrderPayments.length === 0 ? (
                  <p className={classes.detailsEmpty}>אין תשלומים להצגה</p>
                ) : (
                  <div className={classes.detailsTable}>
                    <div className={`${classes.detailsTableHead} ${classes.detailsPaymentsHead}`}>
                      <span>אמצעי תשלום</span><span>סכום</span><span>טיפ</span>
                    </div>
                    {selectedOrderPayments.map((row) => (
                      <div key={row.id} className={`${classes.detailsTableRow} ${classes.detailsPaymentsRow}`}>
                        <span>{paymentMethodLabel(row)}</span>
                        <span>{asCurrency(row.baseAmount)}</span>
                        <span>{asCurrency(collectedTip(row))}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>

            <div className={classes.detailsSectionGroup}>
              <h4 className={classes.detailsSectionTitle}>לוג הדפסות</h4>
              <section className={`${classes.detailsSection} ${classes.detailsSectionCardTone} ${classes.logSectionPlain}`}>
                {printLogs.length === 0 ? (
                  <p className={classes.detailsEmpty}>אין לוגים להצגה</p>
                ) : (
                  <>
                    <div className={`${classes.detailsTableHead} ${classes.detailsLogsHead}`}>
                      <span>זמן</span><span>סוג</span><span>עובד</span><span>מדפסת</span><span>פירוט</span>
                    </div>
                    <div className={classes.logEntries}>
                      {printLogs.map((log) => (
                        <article key={log.id} className={classes.logEntry}>
                          <div className={`${classes.detailsTableRow} ${classes.detailsLogsRow}`}>
                            <span>{asHour(log.occurredAt ?? log.createdAt)}</span>
                            <span>{printKindLabel(log.kind)}</span>
                            <span>{log.workerName ?? '—'}</span>
                            <span>{log.printerName ?? '—'}</span>
                            <span>
                              {log.items.length > 0 ? (
                                <UnstyledButton
                                  className={classes.logDetailsToggle}
                                  onClick={() => togglePrintLogItems(log.id)}
                                  data-testid={`dashboard-breakdown-log-toggle-${log.id}`}
                                >
                                  {`${log.items.length} פריטים`}
                                </UnstyledButton>
                              ) : (
                                '—'
                              )}
                            </span>
                          </div>
                          {expandedPrintLogIds.has(log.id) && log.items.length > 0 ? (
                            <div className={classes.logItemsExpandRow}>
                              <ul className={classes.logItemsList}>
                                {log.items.map((item, index) => (
                                  <li key={`${log.id}-${item.name}-${index}`} className={classes.logItemsListItem}>
                                    <span className={classes.logItemsListItemName}>
                                      <span className={classes.quantityInline}>{`x ${item.quantity}`}</span>
                                      {' '}
                                      {item.name}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </article>
                      ))}
                    </div>
                  </>
                )}
              </section>
            </div>
          </div>
        ) : null}
      </Modal>
    </Modal>
  );
};
